import { AppState, RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import { Browser } from '@capacitor/browser'

import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useHistory } from 'react-router-dom'

import { AUTH0_LOGIN_SCOPE, clearSignupFlow, isNativeApp, markSignupFlowPending } from '../config/auth0'
import { useAppToast } from '../hooks/useAppToast'
import { AuthStateContext, useAuthToken } from '../providers/AuthStateContext'
import UserService from '../services/UserService/UserService'
import { markEmailVerifiedLocally } from '../utils/emailVerification'

export type AuthPromptModalMode = 'auth' | 'verify-email'
export type AuthPromptModalVariant = 'gate' | 'signup'

const VERIFIED_AUTO_CLOSE_MS = 2500

type VerifyCheckState = 'idle' | 'loading' | 'not_verified' | 'error' | 'verified'

interface AuthPromptModalProps {
  /** The URL to return to after login/signup */
  returnTo: string
  mode?: AuthPromptModalMode
  /** `signup` = one-screen post-registration prompt; `gate` = blocks a write action. */
  variant?: AuthPromptModalVariant
  /** Called when the user dismisses the signup prompt (Later / backdrop). */
  onDismiss?: () => void
  /** When set, dismisses the gate modal without navigating (e.g. inline actions on a detail page). */
  onClose?: () => void
}

export const AuthPromptModal: React.FC<AuthPromptModalProps> = ({
  returnTo,
  mode = 'auth',
  variant = 'gate',
  onDismiss,
  onClose,
}) => {
  const { loginWithRedirect, user } = useAuth0()
  const { isTokenReady } = useAuthToken()
  const { showSuccessToast } = useAppToast()
  const isMobileDevice = isNativeApp()
  const history = useHistory()
  const isVerifyEmailMode = mode === 'verify-email'
  const isSignupVariant = variant === 'signup'
  const [resendState, setResendState] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [verifyCheckState, setVerifyCheckState] = useState<VerifyCheckState>('idle')
  const verifyCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingVerifiedCloseRef = useRef(false)
  const showSuccessToastRef = useRef(showSuccessToast)
  showSuccessToastRef.current = showSuccessToast

  useEffect(() => {
    return () => {
      if (verifyCloseTimerRef.current) {
        clearTimeout(verifyCloseTimerRef.current)
        verifyCloseTimerRef.current = null
      }
      // Parent may unmount us right after persist (before the close timer fires).
      if (pendingVerifiedCloseRef.current) {
        pendingVerifiedCloseRef.current = false
        showSuccessToastRef.current('Email verified!')
      }
    }
  }, [])

  const buildLoginParams = (isSignup: boolean): RedirectLoginOptions<AppState> => {
    AuthStateContext.setRedirectUrl(returnTo)
    const base: RedirectLoginOptions<AppState> = {
      appState: { fromSignup: isSignup, redirectUrl: returnTo },
      authorizationParams: {
        scope: AUTH0_LOGIN_SCOPE,
        ...(isSignup ? { screen_hint: 'signup' as const } : {}),
      },
    }
    if (isMobileDevice) {
      return {
        ...base,
        async openUrl(url) {
          await Browser.open({ url, windowName: '_self' })
        },
      }
    }
    return base
  }

  const handleLogin = () => loginWithRedirect(buildLoginParams(false))

  const handleSignUp = () => {
    // Set the signup flag before the redirect so OnboardingModal shows after Auth0 returns.
    // localStorage survives the full-page redirect reliably (same pattern as Register.tsx).
    markSignupFlowPending()
    loginWithRedirect(buildLoginParams(true))
  }

  const handleBack = () => {
    if (onClose) {
      onClose()
      return
    }
    history.goBack()
  }

  const handleDismissSignup = () => {
    onDismiss?.()
  }

  const persistVerificationSuccess = (userId: string) => {
    markEmailVerifiedLocally(userId)
    clearSignupFlow()
  }

  const dismissAfterVerification = () => {
    if (pendingVerifiedCloseRef.current) {
      pendingVerifiedCloseRef.current = false
      showSuccessToast('Email verified!')
    }
    onClose?.()
  }

  const scheduleVerificationSuccessClose = () => {
    pendingVerifiedCloseRef.current = true

    if (verifyCloseTimerRef.current) {
      clearTimeout(verifyCloseTimerRef.current)
    }

    verifyCloseTimerRef.current = setTimeout(() => {
      verifyCloseTimerRef.current = null
      dismissAfterVerification()
    }, VERIFIED_AUTO_CLOSE_MS)
  }

  const handleCloseModal = () => {
    if (verifyCheckState === 'verified') {
      if (verifyCloseTimerRef.current) {
        clearTimeout(verifyCloseTimerRef.current)
        verifyCloseTimerRef.current = null
      }
      dismissAfterVerification()
      return
    }

    if (isSignupVariant) {
      handleDismissSignup()
      return
    }

    handleBack()
  }

  const isVerifyActionDisabled = verifyCheckState === 'loading' || verifyCheckState === 'verified' || !isTokenReady

  const getVerifyButtonLabel = () => {
    if (verifyCheckState === 'loading') {
      return 'Checking…'
    }
    if (verifyCheckState === 'verified') {
      return 'Verified'
    }
    return "I've verified my email"
  }

  const getVerifyButtonClassName = (buttonVariant: 'primary' | 'secondary') => {
    if (verifyCheckState === 'verified') {
      return 'w-full bg-[#10b981] text-white font-semibold py-3 rounded-xl transition-all shadow-md text-sm disabled:opacity-100'
    }

    if (buttonVariant === 'primary') {
      return 'w-full bg-orange text-white font-semibold py-3 rounded-xl hover:bg-opacity-90 transition-all shadow-md text-sm disabled:opacity-60'
    }

    return 'w-full border-2 border-aqua-mint text-aqua-mint font-semibold py-3 rounded-xl hover:bg-aqua-mint hover:text-white transition-all text-sm disabled:opacity-60'
  }

  const handleResendVerification = async () => {
    setResendState('loading')
    setVerifyCheckState('idle')
    try {
      await UserService.resendVerificationEmail()
      setResendState('sent')
    } catch {
      setResendState('error')
    }
  }

  const handleAlreadyVerified = async () => {
    if (!user?.sub || !isTokenReady || !AuthStateContext.getToken()) {
      setVerifyCheckState('error')
      return
    }

    setVerifyCheckState('loading')
    setResendState('idle')

    try {
      const isVerified = await UserService.checkVerificationStatus()
      if (!isVerified) {
        setVerifyCheckState('not_verified')
        return
      }

      setVerifyCheckState('verified')
      persistVerificationSuccess(user.sub)
      scheduleVerificationSuccessClose()
    } catch {
      setVerifyCheckState('error')
    }
  }

  const signupEmail = user?.email || 'your email'

  const modal = (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        inset: 0,
        justifyContent: 'center',
        padding: '0 16px',
        position: 'fixed',
        zIndex: isSignupVariant ? 1000000 : 999999,
      }}
    >
      <div
        onClick={handleCloseModal}
        style={{
          backdropFilter: 'blur(4px)',
          background: 'rgba(0,0,0,0.55)',
          cursor: 'pointer',
          inset: 0,
          position: 'absolute',
        }}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '1rem',
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          maxWidth: '22rem',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          zIndex: 1,
        }}
      >
        <button
          type="button"
          onClick={handleCloseModal}
          className="absolute top-3 right-3 z-10 text-white text-opacity-80 hover:text-opacity-100 transition-colors"
          aria-label="Close modal"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div
          className="px-8 pt-10 pb-8 text-center"
          style={{ background: 'linear-gradient(135deg, #308b97 0%, #3aa8b5 100%)' }}
        >
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
              {isVerifyEmailMode ? (
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              )}
            </div>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {isVerifyEmailMode
              ? isSignupVariant
                ? 'Verify your email'
                : 'Verify your email to continue'
              : 'Sign in to continue'}
          </h2>
          <p className="text-white text-sm" style={{ opacity: 0.85 }}>
            {isVerifyEmailMode
              ? isSignupVariant
                ? `We sent a link to ${signupEmail}. Open your email and tap the link.`
                : 'Check your inbox for the verification link we sent when you signed up.'
              : 'Create a free account or log in to write your review'}
          </p>
        </div>

        <div className="px-8 py-7 space-y-3">
          {isVerifyEmailMode ? (
            isSignupVariant ? (
              <>
                <button
                  onClick={handleAlreadyVerified}
                  disabled={isVerifyActionDisabled}
                  className={getVerifyButtonClassName('primary')}
                >
                  {getVerifyButtonLabel()}
                </button>

                {verifyCheckState === 'not_verified' && (
                  <p className="text-center text-sm text-red-600">
                    We still don&apos;t see a verified email. Check your inbox and try again.
                  </p>
                )}
                {verifyCheckState === 'error' && (
                  <p className="text-center text-sm text-red-600">
                    Could not check verification status. Please try again in a moment.
                  </p>
                )}

                {verifyCheckState !== 'verified' && (
                  <>
                    <button
                      onClick={handleDismissSignup}
                      className="w-full border-2 border-aqua-mint text-aqua-mint font-semibold py-3 rounded-xl hover:bg-aqua-mint hover:text-white transition-all text-sm"
                    >
                      Later
                    </button>

                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendState === 'loading' || resendState === 'sent'}
                      className="w-full text-gray-400 text-sm py-1 hover:text-gray-600 transition-colors disabled:opacity-60"
                    >
                      {resendState === 'loading'
                        ? 'Sending…'
                        : resendState === 'sent'
                          ? 'Verification email sent'
                          : "Didn't get it? Resend email"}
                    </button>

                    {resendState === 'error' && (
                      <p className="text-center text-sm text-red-600">
                        Could not send the email. Please try again in a moment.
                      </p>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {verifyCheckState !== 'verified' && (
                  <>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendState === 'loading' || resendState === 'sent'}
                      className="w-full bg-orange text-white font-semibold py-3 rounded-xl hover:bg-opacity-90 transition-all shadow-md text-sm disabled:opacity-60"
                    >
                      {resendState === 'loading'
                        ? 'Sending…'
                        : resendState === 'sent'
                          ? 'Verification email sent'
                          : 'Resend verification email'}
                    </button>

                    {resendState === 'sent' && (
                      <p className="text-center text-sm text-green-600">Check your inbox for the verification link.</p>
                    )}
                    {resendState === 'error' && (
                      <p className="text-center text-sm text-red-600">
                        Could not send the email. Please try again in a moment.
                      </p>
                    )}
                  </>
                )}

                <button
                  onClick={handleAlreadyVerified}
                  disabled={isVerifyActionDisabled}
                  className={getVerifyButtonClassName('secondary')}
                >
                  {getVerifyButtonLabel()}
                </button>

                {verifyCheckState === 'not_verified' && (
                  <p className="text-center text-sm text-red-600">
                    We still don&apos;t see a verified email. Check your inbox and try again.
                  </p>
                )}
                {verifyCheckState === 'error' && (
                  <p className="text-center text-sm text-red-600">
                    Could not check verification status. Please try again in a moment.
                  </p>
                )}

                {verifyCheckState !== 'verified' && (
                  <>
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-gray-100" />
                      <span className="text-xs text-gray-400">or</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>

                    <button
                      onClick={handleBack}
                      className="w-full text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors"
                    >
                      Go back
                    </button>
                  </>
                )}
              </>
            )
          ) : (
            <>
              <button
                onClick={handleSignUp}
                className="w-full bg-orange text-white font-semibold py-3 rounded-xl hover:bg-opacity-90 transition-all shadow-md text-sm"
              >
                Create a free account
              </button>

              <button
                onClick={handleLogin}
                className="w-full border-2 border-aqua-mint text-aqua-mint font-semibold py-3 rounded-xl hover:bg-aqua-mint hover:text-white transition-all text-sm"
              >
                Log in
              </button>

              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              <button
                onClick={handleBack}
                className="w-full text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors"
              >
                Go back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(modal, document.body)
}
